import { useMemo, type ReactNode } from 'react';
import { detectPII, type PIIFinding } from '@cade/shared-utils/pii';
import { trpc } from '../../api/trpc';

interface AffectedEntity {
  type: string;
  id: string;
  label?: string;
}

interface AuditRecordDetailProps {
  record: {
    id: string;
    sequenceNumber: string;
    eventType?: string;
    actorType?: string;
    actorId?: string | null;
    actorEmail?: string | null;
    resourceId?: string | null;
    ipAddress?: string | null;
    userAgent?: string | null;
    conversationId?: string | null;
    conversationTitle?: string | null;
    parentAuditRecordId?: string | null;
    legalHold?: boolean;
    affectedEntities?: AffectedEntity[] | null;
    details?: Record<string, unknown> | null;
  };
}

/** Message-bearing event types whose body is resolved live for the detail view. */
const MESSAGE_EVENT_TYPES = new Set(['conversation.message_sent', 'conversation.response_generated']);

function piiLabel(type: string): string {
  return type.replace(/_/g, ' ');
}

/** Split `text` into plain segments and <mark>-wrapped potential-PII segments. */
function renderWithPiiHighlights(text: string, findings: PIIFinding[]): ReactNode[] {
  if (findings.length === 0) return [text];
  const sorted = [...findings].sort((a, b) => a.start - b.start);
  const nodes: ReactNode[] = [];
  let cursor = 0;
  sorted.forEach((finding, i) => {
    if (finding.start < cursor) return; // defensive: skip any overlap
    if (finding.start > cursor) nodes.push(text.slice(cursor, finding.start));
    nodes.push(
      <mark
        key={i}
        className="audit-pii-highlight"
        data-pii-type={finding.type}
        title={`Potential ${piiLabel(finding.type)}`}
      >
        {text.slice(finding.start, finding.end)}
      </mark>,
    );
    cursor = finding.end;
  });
  if (cursor < text.length) nodes.push(text.slice(cursor));
  return nodes;
}

export function AuditRecordDetail({ record }: AuditRecordDetailProps) {
  const isMessageEvent = !!record.eventType && MESSAGE_EVENT_TYPES.has(record.eventType);

  // Message body is resolved live from the messages table — not duplicated
  // into the immutable audit record. resourceId is the message id.
  const messageQuery = trpc.audit.getMessage.useQuery(
    { messageId: record.resourceId ?? '' },
    { enabled: isMessageEvent && !!record.resourceId },
  );
  const messageContent = messageQuery.data?.content ?? '';
  const messagePii = useMemo(() => detectPII(messageContent).findings, [messageContent]);

  // Detect PII in the rendered details JSON so highlights line up exactly.
  const detailsJson = useMemo(
    () => (record.details ? JSON.stringify(record.details, null, 2) : ''),
    [record.details],
  );
  const detailsPii = useMemo(() => detectPII(detailsJson), [detailsJson]);
  const piiSummary = useMemo(
    () =>
      Object.entries(detailsPii.counts)
        .filter(([, count]) => count > 0)
        .map(([type, count]) => `${count} ${piiLabel(type)}`)
        .join(', '),
    [detailsPii],
  );

  return (
    <div className="audit-detail">
      <div className="audit-detail-grid">
        <div className="audit-detail-field">
          <span className="audit-detail-label">Record ID</span>
          <span className="audit-detail-value"><code>{record.id}</code></span>
        </div>
        <div className="audit-detail-field">
          <span className="audit-detail-label">Sequence</span>
          <span className="audit-detail-value"><code>{record.sequenceNumber}</code></span>
        </div>
        <div className="audit-detail-field">
          <span className="audit-detail-label">Actor Type</span>
          <span className="audit-detail-value">{record.actorType ?? 'user'}</span>
        </div>
        {record.actorEmail && (
          <div className="audit-detail-field">
            <span className="audit-detail-label">Actor Email</span>
            <span className="audit-detail-value">{record.actorEmail}</span>
          </div>
        )}
        {record.ipAddress && (
          <div className="audit-detail-field">
            <span className="audit-detail-label">IP Address</span>
            <span className="audit-detail-value"><code>{record.ipAddress}</code></span>
          </div>
        )}
        {record.userAgent && (
          <div className="audit-detail-field">
            <span className="audit-detail-label">User Agent</span>
            <span className="audit-detail-value">{record.userAgent}</span>
          </div>
        )}
        {record.conversationId && (
          <div className="audit-detail-field">
            <span className="audit-detail-label">Chat</span>
            <span className="audit-detail-value">
              {record.conversationTitle || 'Untitled chat'}{' '}
              <code>{record.conversationId}</code>
            </span>
          </div>
        )}
        {record.parentAuditRecordId && (
          <div className="audit-detail-field">
            <span className="audit-detail-label">Parent Record</span>
            <span className="audit-detail-value"><code>{record.parentAuditRecordId}</code></span>
          </div>
        )}
      </div>

      {isMessageEvent && (
        <div className="audit-detail-section">
          <h4 className="audit-detail-section-title">Message</h4>
          {messageQuery.isLoading && <p className="audit-detail-muted">Loading message…</p>}
          {messageQuery.isError && (
            <p className="audit-detail-muted">Could not load the message.</p>
          )}
          {!messageQuery.isLoading && !messageQuery.isError && messageQuery.data == null && (
            <p className="audit-detail-muted">Message content unavailable (it may have been deleted).</p>
          )}
          {messageQuery.data != null && (
            <pre className="audit-detail-message">
              {renderWithPiiHighlights(messageContent, messagePii)}
            </pre>
          )}
        </div>
      )}

      {record.affectedEntities && record.affectedEntities.length > 0 && (
        <div className="audit-detail-section">
          <h4 className="audit-detail-section-title">Affected Entities</h4>
          <div>
            {record.affectedEntities.map((entity, i) => (
              <span key={i} className="audit-entity-chip">
                {entity.type}: {entity.label ?? entity.id}
              </span>
            ))}
          </div>
        </div>
      )}

      {record.details && Object.keys(record.details).length > 0 && (
        <div className="audit-detail-section">
          <h4 className="audit-detail-section-title">
            Event Details
            {detailsPii.findings.length > 0 && (
              <span
                className="audit-pii-legend"
                title={`Potential PII: ${piiSummary}`}
              >
                Potential PII
              </span>
            )}
          </h4>
          <pre className="audit-detail-json">
            {renderWithPiiHighlights(detailsJson, detailsPii.findings)}
          </pre>
        </div>
      )}
    </div>
  );
}
